import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatAmountSummary, formatDate } from '@/utils/formatters';
import { apiFetch } from '@/utils/api';
import { ArrowLeft, Edit, Trash2, CheckCircle, Clock, XCircle, RotateCcw, ArrowUp, ArrowDown } from 'lucide-react';
import { CurrentUserProvider } from '@/hooks/useCurrentUser';
import { TagSelector } from '@/components/TagSelector';
import type { Tag } from '@/components/TagBadge';

interface Vendor {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
}

interface CostCenter {
    id: string;
    name: string;
    is_active: boolean;
}

interface PaymentInstrument {
    id: string;
    name: string;
    instrument_type: string;
    account_number?: string;
    bank_name?: string;
}

interface Invoice {
    id: string;
    invoice_number?: string;
    total_amount?: number;
    currency?: string;
}

interface Payment {
    id: string;
    vendor?: Vendor;
    cost_center?: CostCenter;
    payment_instrument?: PaymentInstrument;
    invoice?: Invoice;
    direction: string;
    amount: number;
    currency: string;
    amount_accounting?: number;
    value_date: string;
    received_at?: string;
    status: string;
    invoice_reference?: string;
    external_id?: string;
    created_at: string;
    updated_at?: string;
}

const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
        case 'posted':
            return 'default';
        case 'pending':
            return 'secondary';
        case 'voided':
        case 'reversed':
            return 'destructive';
        default:
            return 'outline';
    }
};

const getStatusIcon = (status: string) => {
    switch (status) {
        case 'posted':
            return <CheckCircle className="h-4 w-4 text-green-600" />;
        case 'pending':
            return <Clock className="h-4 w-4 text-yellow-600" />;
        case 'voided':
            return <XCircle className="h-4 w-4 text-red-600" />;
        case 'reversed':
            return <RotateCcw className="h-4 w-4 text-red-600" />;
        default:
            return null;
    }
};

const getDirectionIcon = (direction: string) => {
    switch (direction) {
        case 'AR':
            return <span title="Accounts Receivable (Money In)"><ArrowDown className="h-4 w-4 text-green-600" /></span>;
        case 'AP':
            return <span title="Accounts Payable (Money Out)"><ArrowUp className="h-4 w-4 text-red-600" /></span>;
        default:
            return null;
    }
};

export default function PaymentDetailPage() {
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
    const { id } = params;
    const [payment, setPayment] = useState<Payment | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [paymentTags, setPaymentTags] = useState<Tag[]>([]);

    useEffect(() => {
        if (id) {
            fetchPayment();
            loadPaymentTags();
        }
    }, [id]);

    const loadPaymentTags = async () => {
        try {
            const response = await apiFetch(`/api/tags/entities/payments/${id}`);
            if (response.ok) {
                const data = await response.json();
                setPaymentTags(data.map((item: any) => item.tag));
            }
        } catch (error) {
            console.error('Failed to load payment tags:', error);
        }
    };

    const fetchPayment = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiFetch(`/api/payments/${id}`);
            if (response.ok) {
                const data = await response.json();
                setPayment(data);
            } else {
                console.error('API response not ok:', response.status, response.statusText);
                setError('Failed to load payment details');
            }
        } catch (error) {
            console.error('Error fetching payment:', error);
            setError('Failed to load payment details');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!payment || !confirm('Are you sure you want to delete this payment?')) {
            return;
        }

        try {
            const res = await apiFetch(`/api/payments/${payment.id}`, {
                method: 'DELETE',
            });
            if (res.ok) {
                navigate({ to: '/payments' });
            } else {
                let msg = 'Failed to delete payment';
                try {
                    const err = await res.json();
                    if (typeof err.detail === 'string') msg = err.detail;
                } catch {
                    /* ignore */
                }
                alert(msg);
            }
        } catch (error) {
            console.error('Error deleting payment:', error);
            alert('Failed to delete payment');
        }
    };

    if (loading) {
        return (
            <CurrentUserProvider>
                <Layout>
                    <div className="space-y-6">
                        <div className="flex items-center space-x-4">
                            <Skeleton className="h-8 w-8" />
                            <Skeleton className="h-8 w-48" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Card>
                                <CardHeader>
                                    <Skeleton className="h-6 w-32" />
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-3/4" />
                                    <Skeleton className="h-4 w-1/2" />
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <Skeleton className="h-6 w-32" />
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-3/4" />
                                    <Skeleton className="h-4 w-1/2" />
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </Layout>
            </CurrentUserProvider>
        );
    }

    if (error || !payment) {
        return (
            <CurrentUserProvider>
                <Layout>
                    <div className="space-y-6">
                        <div className="flex items-center space-x-4">
                            <Button variant="ghost" onClick={() => navigate({ to: '..' })}>
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                            <h1 className="text-3xl font-bold tracking-tight">Payment Not Found</h1>
                        </div>
                        <div className="text-center py-8">
                            <p className="text-muted-foreground">{error || 'Payment not found'}</p>
                            <Button className="mt-4" onClick={() => navigate({ to: '/payments'})}>
                                Back to Payments
                            </Button>
                        </div>
                    </div>
                </Layout>
            </CurrentUserProvider>
        );
    }

    return (
        <CurrentUserProvider>
            <Layout>
                <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <Button variant="ghost" onClick={() => navigate({ to: '..' })}>
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                            <div>
                                <h1 className="text-3xl font-bold tracking-tight">Payment Details</h1>
                                <p className="text-muted-foreground">
                                    {payment.invoice_reference || payment.external_id || `Payment ${payment.id.slice(0, 8)}`}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button variant="outline" onClick={() => navigate({ to: `/payments/${payment.id}/edit` })}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                            </Button>
                            <Button variant="destructive" onClick={handleDelete}>
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                            </Button>
                        </div>
                    </div>

                    {/* Payment Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Basic Information */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Payment Information</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium">Status</span>
                                    <div className="flex items-center space-x-2">
                                        {getStatusIcon(payment.status)}
                                        <Badge variant={getStatusVariant(payment.status)}>
                                            {payment.status}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium">Direction</span>
                                    <div className="flex items-center space-x-2">
                                        {getDirectionIcon(payment.direction)}
                                        <span>{payment.direction === 'AR' ? 'Accounts Receivable' : 'Accounts Payable'}</span>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium">Amount</span>
                                    <span className="font-semibold text-lg">
                                        {formatAmountSummary(payment.amount, payment.currency)}
                                    </span>
                                </div>
                                {payment.amount_accounting && payment.amount_accounting !== payment.amount && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">Accounting Amount</span>
                                        <span className="text-muted-foreground">
                                            {formatAmountSummary(payment.amount_accounting, 'HUF')}
                                        </span>
                                    </div>
                                )}
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium">Value Date</span>
                                    <span>{formatDate(payment.value_date)}</span>
                                </div>
                                {payment.received_at && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">Received At</span>
                                        <span>{formatDate(payment.received_at)}</span>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Related Information */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Related Information</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {payment.vendor && (
                                    <div>
                                        <span className="text-sm font-medium">Vendor</span>
                                        <p className="text-sm text-muted-foreground mt-1">{payment.vendor.name}</p>
                                        {payment.vendor.email && (
                                            <p className="text-sm text-muted-foreground">{payment.vendor.email}</p>
                                        )}
                                    </div>
                                )}
                                {payment.cost_center && (
                                    <div>
                                        <span className="text-sm font-medium">Cost Center</span>
                                        <p className="text-sm text-muted-foreground mt-1">{payment.cost_center.name}</p>
                                    </div>
                                )}
                                {payment.payment_instrument && (
                                    <div>
                                        <span className="text-sm font-medium">Payment Method</span>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            {payment.payment_instrument.name}
                                            {payment.payment_instrument.account_number && (
                                                <span className="ml-2">({payment.payment_instrument.account_number})</span>
                                            )}
                                        </p>
                                    </div>
                                )}
                                {payment.invoice_reference && (
                                    <div>
                                        <span className="text-sm font-medium">Invoice Reference</span>
                                        <p className="text-sm text-muted-foreground mt-1">{payment.invoice_reference}</p>
                                    </div>
                                )}
                                {payment.external_id && (
                                    <div>
                                        <span className="text-sm font-medium">External ID</span>
                                        <p className="text-sm text-muted-foreground mt-1">{payment.external_id}</p>
                                    </div>
                                )}
                                <div>
                                    <span className="text-sm font-medium">Created</span>
                                    <p className="text-sm text-muted-foreground mt-1">{formatDate(payment.created_at)}</p>
                                </div>
                                {payment.updated_at && (
                                    <div>
                                        <span className="text-sm font-medium">Last Updated</span>
                                        <p className="text-sm text-muted-foreground mt-1">{formatDate(payment.updated_at)}</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Tags */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Tags</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <TagSelector
                                entityType="payments"
                                entityId={id as string}
                                selectedTags={paymentTags}
                                onChange={(tags) => {
                                    setPaymentTags(tags);
                                }}
                            />
                        </CardContent>
                    </Card>

                    {/* Linked Invoice */}
                    {payment.invoice && (
                        <Card>
                            <CardHeader>
                                <CardTitle>Linked Invoice</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium">{payment.invoice.invoice_number}</p>
                                        <p className="text-sm text-muted-foreground">
                                            {formatAmountSummary(payment.invoice.total_amount || 0, payment.invoice.currency || payment.currency)}
                                        </p>
                                    </div>
                                    <Button variant="outline" onClick={() => navigate({ to: `/invoices/${payment.invoice?.id}`})}>
                                        View Invoice
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </Layout>
        </CurrentUserProvider>
    );
}

export const Route = createFileRoute('/payments/$id')({
  component: PaymentDetailPage,
})
